import { clearAuth, getToken, getStoredUser } from './auth'
import { peekHubShellCache, setHubShellCache } from './hubShellCache'
import type {
  HubArvorePayload,
  HubAvisoDetalhe,
  HubAvisoResumo,
  HubConvidadosPayload,
  HubEnqueteDetalhe,
  HubLegislacaoDetalhe,
  HubLegislacaoPayload,
  HubEnquetesPayload,
  HubEventoDetalhe,
  HubEventosPayload,
  HubEquipePayload,
  HubBiografiaPayload,
  HubCadastroCep,
  HubCadastroOpcao,
  HubCadastroPayload,
  HubMedalhasPayload,
  HubMidiaListPayload,
  HubMissoesPayload,
  HubNoticiaDetalhe,
  HubNoticiaResumo,
  HubPayload,
  HubInicioPayload,
  HubPontuacaoPayload,
  HubRankingPayload,
  HubRedesPayload,
  HubSolicitacaoDetalhe,
  HubSolicitacoesPayload,
  HubVideoDetalhe,
  HubVideoResumo,
  LoginResponse,
} from '../types'

type ApiEnvelope<T> = {
  success?: boolean
  message?: string
  data?: T
  error?: string
}

async function parseJson<T>(res: Response): Promise<ApiEnvelope<T>> {
  const text = await res.text()
  try {
    return JSON.parse(text) as ApiEnvelope<T>
  } catch {
    throw new Error(res.ok ? 'Resposta inválida do servidor.' : `Erro HTTP ${res.status}`)
  }
}

export async function apiLogin(
  identifier: string,
  password: string,
  opts?: { mode?: 'email' | 'phone' },
): Promise<LoginResponse> {
  const trimmed = identifier.trim()
  const body: Record<string, string> = { password }
  const asEmail = opts?.mode === 'email' || (opts?.mode !== 'phone' && trimmed.includes('@'))
  if (asEmail) {
    body.email = trimmed
  } else {
    body.phone = trimmed.replace(/\D/g, '')
  }

  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJson(res) as Promise<LoginResponse>
}

export async function apiValidate(): Promise<boolean> {
  const token = getToken()
  if (!token) return false
  const res = await fetch('/api/v1/auth/validate', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    clearAuth()
    return false
  }
  const json = await parseJson<{ valid?: boolean }>(res)
  return Boolean(json.success || json.data?.valid)
}

async function apiGetAuth<T>(path: string, fallback: string): Promise<T> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch(path, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<T>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || fallback)
  }
  return json.data
}

export async function apiHub(opts?: { force?: boolean }): Promise<HubPayload> {
  const userId = getStoredUser()?.id ?? 0
  if (!opts?.force && userId > 0) {
    const cached = peekHubShellCache(userId)
    if (cached) return cached
  }
  const data = await apiGetAuth<HubPayload>('/api/v1/hub', 'Não foi possível carregar o hub.')
  if (userId > 0) setHubShellCache(userId, data)
  return data
}

/** Blocos pesados da home — mesclar no shell com mergeHubInicio. */
export async function apiHubInicio(): Promise<HubInicioPayload> {
  return apiGetAuth<HubInicioPayload>('/api/v1/hub/inicio', 'Não foi possível carregar o início.')
}

export function mergeHubInicio(shell: HubPayload, inicio: HubInicioPayload): HubPayload {
  return {
    ...shell,
    ...inicio,
    urls: { ...(shell.urls ?? {}), ...(inicio.urls ?? {}) },
    inicio_loaded: true,
  }
}

export async function apiConcluirStartWizard(): Promise<HubPayload> {
  const token = getToken()
  if (!token) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const res = await fetch('/api/v1/hub/start-wizard', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível concluir o onboarding.')
  }
  return { ...json.data, inicio_loaded: true }
}

export async function apiEquipe(): Promise<HubEquipePayload> {
  return apiGetAuth<HubEquipePayload>('/api/v1/hub/equipe', 'Não foi possível carregar a equipe.')
}

export async function apiArvore(page = 1, q = ''): Promise<HubArvorePayload> {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (q.trim()) params.set('q', q.trim())
  const qs = params.toString() ? `?${params}` : ''
  return apiGetAuth<HubArvorePayload>(`/api/v1/hub/arvore${qs}`, 'Não foi possível carregar a árvore.')
}

export async function apiArvoreBusca(q: string, page = 1): Promise<HubArvorePayload> {
  const params = new URLSearchParams()
  params.set('q', q.trim())
  if (page > 1) params.set('page', String(page))
  return apiGetAuth<HubArvorePayload>(
    `/api/v1/hub/arvore/busca?${params}`,
    'Não foi possível buscar na árvore.',
  )
}

export async function apiArvoreNivel(
  nivel: number,
  pessoa: number,
  page = 1,
  q = '',
): Promise<HubArvorePayload> {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (q.trim()) params.set('q', q.trim())
  const qs = params.toString() ? `?${params}` : ''
  return apiGetAuth<HubArvorePayload>(
    `/api/v1/hub/arvore/nivel/${nivel}/${pessoa}${qs}`,
    'Não foi possível carregar este nível.',
  )
}

export async function apiConvidados(page = 1, q = ''): Promise<HubConvidadosPayload> {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (q.trim()) params.set('q', q.trim())
  const qs = params.toString() ? `?${params}` : ''
  return apiGetAuth<HubConvidadosPayload>(
    `/api/v1/hub/convidados${qs}`,
    'Não foi possível carregar os convidados.',
  )
}

export async function apiTrocarSenhaConvidado(id: number, senha: string): Promise<string> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch('/api/v1/hub/convidados/senha', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id, senha }),
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<Record<string, never>>(res)
  if (!json.success) {
    throw new Error(json.error || json.message || 'Não foi possível alterar a senha.')
  }
  return json.message || 'Senha alterada com sucesso.'
}

export async function apiLgpdExclusao(confirmLgpd: string): Promise<string> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch('/api/v1/hub/lgpd-exclusao', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ confirm_lgpd: confirmLgpd }),
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<{ logout?: boolean }>(res)
  if (!json.success) {
    throw new Error(json.error || json.message || 'Não foi possível processar a exclusão.')
  }
  return json.message || 'Dados processados. Você foi desconectado.'
}

export async function apiMedalhas(params: {
  busca?: string
  posicao?: number
  estrelas?: number
  page?: number
}): Promise<HubMedalhasPayload> {
  const q = new URLSearchParams()
  if (params.busca) q.set('busca', params.busca)
  if (params.posicao) q.set('posicao', String(params.posicao))
  if (params.estrelas) q.set('estrelas', String(params.estrelas))
  if (params.page && params.page > 1) q.set('page', String(params.page))
  const qs = q.toString()
  return apiGetAuth<HubMedalhasPayload>(
    `/api/v1/hub/medalhas${qs ? `?${qs}` : ''}`,
    'Não foi possível carregar as medalhas.',
  )
}

export async function apiRanking(params: {
  periodo?: string
  page?: number
}): Promise<HubRankingPayload> {
  const q = new URLSearchParams()
  if (params.periodo) q.set('periodo', params.periodo)
  if (params.page && params.page > 1) q.set('page', String(params.page))
  const qs = q.toString()
  return apiGetAuth<HubRankingPayload>(
    `/api/v1/hub/ranking${qs ? `?${qs}` : ''}`,
    'Não foi possível carregar o ranking.',
  )
}

export async function apiPontuacao(): Promise<HubPontuacaoPayload> {
  return apiGetAuth<HubPontuacaoPayload>('/api/v1/hub/pontuacao', 'Não foi possível carregar a pontuação.')
}

export async function apiSolicitacoes(page = 1): Promise<HubSolicitacoesPayload> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth<HubSolicitacoesPayload>(
    `/api/v1/hub/solicitacoes${qs}`,
    'Não foi possível carregar as solicitações.',
  )
}

export async function apiSolicitacao(id: number): Promise<HubSolicitacaoDetalhe> {
  return apiGetAuth<HubSolicitacaoDetalhe>(
    `/api/v1/hub/solicitacoes/${id}`,
    'Não foi possível carregar a solicitação.',
  )
}

export async function apiCriarSolicitacao(body: {
  assunto: string
  solicitacao: string
  anexo?: File | null
}): Promise<HubSolicitacaoDetalhe> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }

  let payload: BodyInit
  if (body.anexo) {
    const form = new FormData()
    form.append('assunto', body.assunto)
    form.append('solicitacao', body.solicitacao)
    form.append('anexo', body.anexo)
    payload = form
  } else {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify({
      assunto: body.assunto,
      solicitacao: body.solicitacao,
    })
  }

  const res = await fetch('/api/v1/hub/solicitacoes', {
    method: 'POST',
    headers,
    body: payload,
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubSolicitacaoDetalhe>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível criar a solicitação.')
  }
  return json.data
}

export async function apiMissoes(): Promise<HubMissoesPayload> {
  return apiGetAuth<HubMissoesPayload>('/api/v1/hub/missoes', 'Não foi possível carregar as missões.')
}

export async function apiConcluirMissao(id: number): Promise<HubMissoesPayload> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch(`/api/v1/hub/missoes/${id}/concluir`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubMissoesPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível concluir a missão.')
  }
  return json.data
}

export async function apiNoticias(page = 1): Promise<HubMidiaListPayload<HubNoticiaResumo>> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/noticias${qs}`, 'Não foi possível carregar as notícias.')
}

export async function apiNoticia(id: number): Promise<HubNoticiaDetalhe> {
  return apiGetAuth(`/api/v1/hub/noticias/${id}`, 'Não foi possível carregar a notícia.')
}

export async function apiVideos(page = 1): Promise<HubMidiaListPayload<HubVideoResumo>> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/videos${qs}`, 'Não foi possível carregar os vídeos.')
}

export async function apiVideo(id: number): Promise<HubVideoDetalhe> {
  return apiGetAuth(`/api/v1/hub/videos/${id}`, 'Não foi possível carregar o vídeo.')
}

export async function apiLegislacao(page = 1): Promise<HubLegislacaoPayload> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/legislacao${qs}`, 'Não foi possível carregar o legislativo.')
}

export async function apiLegislacaoItem(id: number): Promise<HubLegislacaoDetalhe> {
  return apiGetAuth(`/api/v1/hub/legislacao/${id}`, 'Não foi possível carregar o documento.')
}

export async function apiEnquetes(page = 1): Promise<HubEnquetesPayload> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/enquetes${qs}`, 'Não foi possível carregar as enquetes.')
}

export async function apiEnquete(id: number): Promise<HubEnqueteDetalhe> {
  return apiGetAuth(`/api/v1/hub/enquetes/${id}`, 'Não foi possível carregar a enquete.')
}

export async function apiVotarEnquete(
  id: number,
  body: {
    resposta?: string
    opcao_selecionada?: string
    opcao_index?: number
    valor_resposta?: number
  },
): Promise<HubEnqueteDetalhe> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch(`/api/v1/hub/enquetes/${id}/votar`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubEnqueteDetalhe>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível registrar o voto.')
  }
  return json.data
}

export async function apiEventos(page = 1): Promise<HubEventosPayload> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/eventos${qs}`, 'Não foi possível carregar os eventos.')
}

export async function apiEvento(id: number): Promise<HubEventoDetalhe> {
  return apiGetAuth(`/api/v1/hub/eventos/${id}`, 'Não foi possível carregar o evento.')
}

export async function apiCriarEvento(input: {
  nome: string
  descricao?: string
  data_evento: string
  hora_inicio: string
  endereco_completo?: string
  imagem?: File | null
}): Promise<HubEventoDetalhe> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }

  let payload: BodyInit
  if (input.imagem) {
    const form = new FormData()
    form.append('nome', input.nome)
    form.append('descricao', input.descricao ?? '')
    form.append('data_evento', input.data_evento)
    form.append('hora_inicio', input.hora_inicio)
    form.append('endereco_completo', input.endereco_completo ?? '')
    form.append('imagem', input.imagem)
    payload = form
  } else {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify({
      nome: input.nome,
      descricao: input.descricao ?? '',
      data_evento: input.data_evento,
      hora_inicio: input.hora_inicio,
      endereco_completo: input.endereco_completo ?? '',
    })
  }

  const res = await fetch('/api/v1/hub/eventos', {
    method: 'POST',
    headers,
    body: payload,
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubEventoDetalhe>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível criar o evento.')
  }
  return json.data
}

export async function apiAtualizarEvento(
  id: number,
  input: {
    nome: string
    descricao?: string
    data_evento: string
    hora_inicio: string
    endereco_completo?: string
    imagem?: File | null
  },
): Promise<HubEventoDetalhe> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }

  let payload: BodyInit
  if (input.imagem) {
    const form = new FormData()
    form.append('nome', input.nome)
    form.append('descricao', input.descricao ?? '')
    form.append('data_evento', input.data_evento)
    form.append('hora_inicio', input.hora_inicio)
    form.append('endereco_completo', input.endereco_completo ?? '')
    form.append('imagem', input.imagem)
    payload = form
  } else {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify({
      nome: input.nome,
      descricao: input.descricao ?? '',
      data_evento: input.data_evento,
      hora_inicio: input.hora_inicio,
      endereco_completo: input.endereco_completo ?? '',
    })
  }

  const res = await fetch(`/api/v1/hub/eventos/${id}`, {
    method: 'POST',
    headers,
    body: payload,
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubEventoDetalhe>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível salvar o evento.')
  }
  return json.data
}

export async function apiRedes(): Promise<HubRedesPayload> {
  return apiGetAuth('/api/v1/hub/redes', 'Não foi possível carregar as redes sociais.')
}

export async function apiSeguirRede(rede: string): Promise<HubRedesPayload> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch(`/api/v1/hub/redes/${encodeURIComponent(rede)}/seguir`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: '{}',
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubRedesPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível confirmar o seguimento.')
  }
  return json.data
}

export async function apiEngajamentoRede(input: {
  tipo_acao: string
  url_post?: string
  obs?: string
  evidencia: File
}): Promise<{ message: string; data: HubRedesPayload }> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const form = new FormData()
  form.append('tipo_acao', input.tipo_acao)
  if (input.url_post) form.append('url_post', input.url_post)
  if (input.obs) form.append('obs', input.obs)
  form.append('evidencia', input.evidencia)
  const res = await fetch('/api/v1/hub/redes/engajamento', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: form,
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubRedesPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível enviar a participação.')
  }
  return { message: json.message || 'Enviado!', data: json.data }
}

export async function apiAvisos(page = 1): Promise<HubMidiaListPayload<HubAvisoResumo>> {
  const qs = page > 1 ? `?page=${page}` : ''
  return apiGetAuth(`/api/v1/hub/avisos${qs}`, 'Não foi possível carregar os avisos.')
}

export async function apiAviso(id: number): Promise<HubAvisoDetalhe> {
  return apiGetAuth(`/api/v1/hub/avisos/${id}`, 'Não foi possível carregar o aviso.')
}

export async function apiBiografia(): Promise<HubBiografiaPayload> {
  return apiGetAuth('/api/v1/hub/biografia', 'Não foi possível carregar a biografia.')
}

export async function apiHubBlob(path: string): Promise<string> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  if (!res.ok) {
    throw new Error('Não foi possível carregar a imagem.')
  }
  return URL.createObjectURL(await res.blob())
}

export async function apiCompartilhar(
  tipo: 'noticia' | 'video' | 'moldura' | 'colinha',
  id: number,
  canal: 'whatsapp' | 'facebook' | 'x' | 'copiar' | 'outro',
): Promise<void> {
  const token = getToken()
  if (!token || id < 1) return
  try {
    await fetch('/api/v1/hub/conteudo/compartilhar', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tipo, id, canal }),
    })
  } catch {
    /* abre o share mesmo se o registro falhar */
  }
}

export async function apiCadastro(): Promise<HubCadastroPayload> {
  return apiGetAuth('/api/v1/hub/cadastro', 'Não foi possível carregar o cadastro.')
}

export async function apiSalvarCadastro(pessoa: HubCadastroPayload['pessoa']): Promise<HubCadastroPayload> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch('/api/v1/hub/cadastro', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(pessoa),
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubCadastroPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Não foi possível salvar o cadastro.')
  }
  return json.data
}

export async function apiTrocarSenha(body: {
  senha_atual: string
  nova_senha: string
  confirma_senha: string
}): Promise<string> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const res = await fetch('/api/v1/hub/senha', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<Record<string, never>>(res)
  if (!json.success) {
    throw new Error(json.error || json.message || 'Não foi possível alterar a senha.')
  }
  return json.message || 'Senha alterada com sucesso!'
}

export async function apiCadastroMunicipios(uf: string): Promise<HubCadastroOpcao[]> {
  const data = await apiGetAuth<{ municipios: HubCadastroOpcao[] }>(
    `/api/v1/hub/cadastro/municipios?uf=${encodeURIComponent(uf)}`,
    'Não foi possível carregar os municípios.',
  )
  return data.municipios ?? []
}

export async function apiCadastroCidades(uf: string): Promise<HubCadastroOpcao[]> {
  const data = await apiGetAuth<{ cidades: HubCadastroOpcao[] }>(
    `/api/v1/hub/cadastro/cidades?uf=${encodeURIComponent(uf)}`,
    'Não foi possível carregar as cidades.',
  )
  return data.cidades ?? []
}

export async function apiCadastroBairros(uf: string, idMunic: number): Promise<HubCadastroPayload['opcoes']['bairros']> {
  const q = new URLSearchParams({ uf, id_munic: String(idMunic) })
  const data = await apiGetAuth<{ bairros: HubCadastroPayload['opcoes']['bairros'] }>(
    `/api/v1/hub/cadastro/bairros?${q}`,
    'Não foi possível carregar os bairros.',
  )
  return data.bairros ?? []
}

export async function apiCadastroCep(cep: string): Promise<HubCadastroCep> {
  return apiGetAuth(`/api/v1/hub/cadastro/cep?cep=${encodeURIComponent(cep)}`, 'CEP não encontrado.')
}

export async function apiUploadFoto(file: File): Promise<HubPayload> {
  const token = getToken()
  if (!token) throw new Error('Não autenticado')
  const form = new FormData()
  form.append('imagem', file)
  const res = await fetch('/api/v1/hub/foto', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: form,
  })
  if (res.status === 401) {
    clearAuth()
    throw new Error('Sessão expirada. Entre novamente.')
  }
  const json = await parseJson<HubPayload>(res)
  if (!json.success || !json.data) {
    throw new Error(json.error || json.message || 'Falha ao enviar foto.')
  }
  const data = { ...json.data, inicio_loaded: true as const }
  // Evita avatar “preso” em 404/CDN antigo logo após o upload (R2 defer).
  if (data.foto?.url) {
    const sep = data.foto.url.includes('?') ? '&' : '?'
    data.foto = { ...data.foto, url: `${data.foto.url}${sep}t=${Date.now()}` }
  }
  return data
}
